import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface OracleCard {
  id?: string;
  title: string;
  message: string;
  category: string;
  image_url?: string;
  keywords: string[];
  is_published: boolean;
  sort_order: number;
}

export default function OracleCardsManager() {
  const [cards, setCards] = useState<OracleCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCard, setEditingCard] = useState<OracleCard | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const defaultCard: OracleCard = {
    title: '',
    message: '',
    category: 'general',
    keywords: [],
    is_published: true,
    sort_order: 0
  };

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      const { data, error } = await supabase
        .from('oracle_cards')
        .select('*')
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setCards(data || []);
    } catch (error) {
      console.error('Error loading cards:', error);
      toast.error('Failed to load oracle cards');
    } finally {
      setLoading(false);
    }
  };

  const saveCard = async (card: OracleCard) => {
    try {
      if (card.id) {
        const { error } = await supabase
          .from('oracle_cards')
          .update(card)
          .eq('id', card.id);
        if (error) throw error;
        toast.success('Card updated successfully');
      } else {
        const { error } = await supabase
          .from('oracle_cards')
          .insert([card]);
        if (error) throw error;
        toast.success('Card created successfully');
      }
      
      loadCards();
      setEditingCard(null);
      setIsCreating(false);
    } catch (error) {
      console.error('Error saving card:', error);
      toast.error('Failed to save card');
    }
  };

  const deleteCard = async (id: string) => {
    try {
      const { error } = await supabase
        .from('oracle_cards')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      toast.success('Card deleted successfully');
      loadCards();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('Failed to delete card');
    }
  };

  const CardForm = ({ card, onSave, onCancel }: { 
    card: OracleCard; 
    onSave: (card: OracleCard) => void; 
    onCancel: () => void; 
  }) => {
    const [formData, setFormData] = useState(card);

    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>{card.id ? 'Edit Oracle Card' : 'Create New Oracle Card'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Card title"
            />
          </div>
          
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Inspirational message for the card"
              rows={4}
            />
          </div>
          
          <div>
            <Label htmlFor="category">Category</Label>
            <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="love">Love</SelectItem>
                <SelectItem value="career">Career</SelectItem>
                <SelectItem value="spirituality">Spirituality</SelectItem>
                <SelectItem value="healing">Healing</SelectItem>
                <SelectItem value="guidance">Guidance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="keywords">Keywords (comma separated)</Label>
            <Input
              id="keywords"
              value={formData.keywords.join(', ')}
              onChange={(e) => setFormData({ 
                ...formData, 
                keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k) 
              })}
              placeholder="love, guidance, healing"
            />
          </div>
          
          <div>
            <Label htmlFor="image_url">Image URL (optional)</Label>
            <Input
              id="image_url"
              value={formData.image_url || ''}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="is_published"
              checked={formData.is_published}
              onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
            />
            <Label htmlFor="is_published">Published</Label>
          </div>
          
          <div>
            <Label htmlFor="sort_order">Sort Order</Label>
            <Input
              id="sort_order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            />
          </div>
          
          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)}>
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading oracle cards...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Oracle Cards Management</h2>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add New Card
        </Button>
      </div>
      
      {isCreating && (
        <CardForm
          card={defaultCard}
          onSave={saveCard}
          onCancel={() => setIsCreating(false)}
        />
      )}
      
      {editingCard && (
        <CardForm
          card={editingCard}
          onSave={saveCard}
          onCancel={() => setEditingCard(null)}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => (
          <Card key={card.id} className={!card.is_published ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{card.title}</CardTitle>
                  <Badge variant="secondary">{card.category}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingCard(card)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => card.id && deleteCard(card.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">{card.message}</p>
              {card.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {card.keywords.map((keyword, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              )}
              <div className="mt-2 text-xs text-muted-foreground">
                Sort: {card.sort_order} | {card.is_published ? 'Published' : 'Draft'}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}